import { useState, useCallback, useRef, useEffect } from 'react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ChevronDown, FileText, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { SourcesPanel } from './SourcesPanel';
import { EditorPane } from './EditorPane';
import { VeraChat } from './VeraChat';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Json } from '@/integrations/supabase/types';

interface ComposerLayoutProps {
  sessionId: string | null;
  draftId: string | null;
  policyId: string | null;
  sessionName: string;
  initialHtml: string;
  initialJson: Json | null;
  originalFileUrl: string | null;
  updateSessionName: (name: string) => Promise<void>;
  saveDraft: (html: string, json: Json | null) => Promise<boolean | undefined>;
}

export function ComposerLayout({
  sessionId,
  draftId,
  policyId,
  sessionName,
  initialHtml,
  initialJson,
  originalFileUrl,
  updateSessionName,
  saveDraft,
}: ComposerLayoutProps) {
  const navigate = useNavigate();
  const { session, profile } = useAuth();
  const [name, setName] = useState(sessionName);
  const [editorEmpty, setEditorEmpty] = useState(true);
  const [exitOpen, setExitOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasApprovedVersion, setHasApprovedVersion] = useState<boolean | null>(null);
  const editorHtmlRef = useRef(initialHtml);
  const editorJsonRef = useRef<Json | null>(initialJson);

  // Detect whether an approved version exists for this policy
  useEffect(() => {
    if (!policyId) return;
    supabase
      .from('policy_versions')
      .select('id')
      .eq('policy_id', policyId)
      .eq('version_status', 'approved')
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setHasApprovedVersion(!!data));
  }, [policyId]);

  const [searchParams] = useSearchParams();

  // When policyId transitions from null to a value (first save on a ?new=<uuid>
  // URL), swap the URL to ?policy_id=<id> so hard refresh / navigate-back loads
  // the draft via Branch 1 instead of restarting pending. Uses navigate({replace: true})
  // rather than window.history.replaceState so react-router's searchParams stays in sync
  // — history.replaceState would leave searchParams stale and any unrelated
  // re-render (e.g., window resize triggering setIsNarrow) would then render
  // ComposerSelector and destroy the editor.
  useEffect(() => {
    if (!policyId) return;
    if (searchParams.get('policy_id') === policyId) return;
    if (!searchParams.get('new')) return;
    navigate(`/composer?policy_id=${policyId}`, { replace: true });
  }, [policyId, searchParams, navigate]);

  const handleNameBlur = () => {
    const trimmed = name.trim() || 'Untitled Policy';
    setName(trimmed);
    updateSessionName(trimmed);
  };

  const handleManualSave = useCallback(() => {
    (window as any).__composerManualSave?.();
  }, []);

  const handleExportPdf = async () => {
    if (!policyId || !session?.access_token) {
      toast.error('Please save your draft before exporting.');
      return;
    }
    const toastId = toast.loading('Generating PDF...');
    try {
      (window as any).__composerManualSave?.();
      await new Promise((r) => setTimeout(r, 500));

      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/trigger-html-conversion`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            policy_id: policyId,
            content_html: editorHtmlRef.current,
          }),
        }
      );

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        console.error('PDF export failed:', errBody.error || res.status);
        throw new Error('Conversion failed');
      }

      const { pdf_url } = await res.json();
      const { data: signedData } = await supabase.storage
        .from('policy-documents')
        .createSignedUrl(pdf_url, 60);

      if (!signedData?.signedUrl) {
        throw new Error('Failed to generate download link');
      }

      const response = await fetch(signedData.signedUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const filename = `${name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);

      toast.dismiss(toastId);
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.dismiss(toastId);
      toast.error('PDF generation is temporarily unavailable. Please try again in a few minutes.');
    }
  };

  const handleDeleteOrDiscard = async () => {
    if (!policyId || !sessionId) return;
    const { data, error } = await supabase.rpc('delete_or_discard_composer_draft', {
      p_policy_id: policyId,
      p_session_id: sessionId,
    });

    if (error) {
      toast.error(error.message || 'Failed to delete draft.');
      return;
    }
    const flow = (data as any)?.flow;
    toast.success(flow === 'discard' ? 'Draft discarded.' : 'Draft deleted.');
    navigate('/policies');
  };

  // Track editor content for submit
  const handleSave = useCallback(
    async (html: string, json: Json | null) => {
      editorHtmlRef.current = html;
      editorJsonRef.current = json;
      return saveDraft(html, json);
    },
    [saveDraft]
  );

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Page header */}
      <div className="flex items-center justify-between px-6 py-4 bg-muted flex-shrink-0">
        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
          <FileText className="h-5 w-5 text-primary flex-shrink-0" />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            className="flex-1 min-w-0 bg-transparent border-none outline-none text-xl font-semibold truncate focus:ring-1 focus:ring-ring rounded px-1 -ml-1 text-foreground"
            placeholder="Untitled Policy"
          />
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Exit */}
          <Button variant="ghost" className="text-muted-foreground hover:text-foreground hover:bg-transparent" onClick={() => setExitOpen(true)}>
            Exit
          </Button>
          <AlertDialog open={exitOpen} onOpenChange={setExitOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Exit Policy Builder?</AlertDialogTitle>
                <AlertDialogDescription>Your draft has been saved. Exit Policy Builder?</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => navigate('/dashboard')}>Exit</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Save Draft */}
          <Button variant="outline" onClick={handleManualSave}>
            Save Draft
          </Button>

          {/* Export PDF */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-1">
                Export <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportPdf}>Export as PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => toast.info('Export coming soon.')}>Export as Word</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Delete / Discard Draft */}
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5"
            onClick={() => setDeleteOpen(true)}
            disabled={hasApprovedVersion === null}
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">
              {hasApprovedVersion ? 'Discard Draft' : 'Delete Draft'}
            </span>
          </Button>
          <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {hasApprovedVersion ? 'Discard Draft?' : 'Delete Draft?'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {hasApprovedVersion
                    ? 'Discard this draft? The current approved version will remain unchanged.'
                    : 'Delete this draft? This policy has never been approved and will be permanently removed.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteOrDiscard}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {hasApprovedVersion ? 'Discard' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Submit */}
          <Button
            onClick={async () => {
              if (!policyId || !profile?.clinic_id || !session?.access_token) {
                toast.error('Missing session or policy data.');
                return;
              }
              setSubmitting(true);
              try {
                handleManualSave();
                // Small delay to let save flush
                await new Promise((r) => setTimeout(r, 300));

                const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
                const res = await fetch(
                  `https://${projectId}.supabase.co/functions/v1/trigger-html-conversion`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                      policy_id: policyId,
                      clinic_id: profile.clinic_id,
                      content_html: editorHtmlRef.current,
                    }),
                  }
                );

                if (!res.ok) {
                  const err = await res.json().catch(() => ({}));
                  throw new Error(err.error || 'Conversion failed');
                }

                const { pdf_url } = await res.json();

                // In mode=update, never pass an existing version id — CreatePolicy must INSERT a new row
                const urlMode = new URLSearchParams(window.location.search).get('mode');
                let versionId: string | null = null;

                if (urlMode !== 'update') {
                  const { data: existingVersion } = await supabase
                    .from('policy_versions')
                    .select('id')
                    .eq('policy_id', policyId)
                    .order('version_number', { ascending: false })
                    .limit(1)
                    .maybeSingle();
                  versionId = existingVersion?.id ?? null;
                }

                navigate('/policies/new', {
                  state: {
                    composerPolicyName: name,
                    composerPolicyId: policyId,
                    composerPdfUrl: pdf_url,
                    composerVersionId: versionId,
                    composerMode: urlMode,
                  },
                });
              } catch (err: any) {
                console.error('HTML conversion error:', err);
                toast.error(err.message || 'Failed to convert document.');
              } finally {
                setSubmitting(false);
              }
            }}
            disabled={editorEmpty || submitting || !policyId}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {submitting ? 'Converting…' : 'Submit for Approval'}
          </Button>
        </div>
      </div>

      {/* Three-pane area */}
      <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0 bg-muted">
        {/* Left: Sources */}
        <ResizablePanel defaultSize={18} minSize={12} maxSize={30}>
          <div className="h-full mx-2 mt-2 rounded-lg shadow-sm bg-card overflow-hidden">
            <SourcesPanel sessionId={sessionId} />
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle className="bg-transparent w-px" />

        {/* Center: Chat placeholder */}
        <ResizablePanel defaultSize={28} minSize={20} maxSize={45} className="[overflow:visible_!important]">
          <div className="h-full mx-2 mt-2 rounded-lg shadow-sm bg-card overflow-hidden">
            <VeraChat
              sessionId={sessionId}
              clinicId={profile?.clinic_id ?? ''}
              documentContent={editorHtmlRef.current}
            />
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle className="bg-transparent w-px" />

        {/* Right: Editor */}
        <ResizablePanel defaultSize={54} minSize={30}>
          <div className="h-full mx-2 mt-2 rounded-lg shadow-sm bg-card overflow-hidden">
            <EditorPane
              initialHtml={initialHtml}
              initialJson={initialJson}
              originalFileUrl={originalFileUrl}
              policyId={policyId}
              clinicId={profile?.clinic_id ?? null}
              sessionId={sessionId}
              onSave={handleSave}
              onEmptyChange={setEditorEmpty}
            />
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
